// ---------------------------------------------------------------------------
// AvatarDisplay — Exibe foto de perfil, avatar preset ou fallback com iniciais.
// Para uso em páginas de UI (Tailwind). NÃO usar dentro de templates
// de exportação (html-to-image exige inline styles sem oklch).
//
// Avatar Lottie (PRO): quando `photoURL` aponta para `.lottie`/`.json`, o
// render delega para `LottieAvatar` lazy-loaded. Animação controlada por
// `animate` (sempre) ou `animateOnHover` (toca enquanto cursor sobre).
// ---------------------------------------------------------------------------

import { lazy, Suspense, useEffect, useState, type ReactNode } from "react";
import { isLottieUrl } from "../../utils/isLottieUrl";
import { lottieStillCache } from "./lottieStill";
import {
  avatarBorder,
  FOUNDER_BORDER_ID,
  resolveAvatarBorderId,
  resolveBorderRingPx,
  DEFAULT_BORDER_RING_PX,
} from "../../features/pro/constants/avatarBorders";
import { AvatarBorderRing } from "./AvatarBorderRing";
import { cloudinaryImageUrl, pxFromSizeClassName } from "../../utils/cloudinaryUrl";

/**
 * Tamanho assumido quando a classe não declara um (`h-full`, classe
 * arbitrária). 112px é o hero do perfil, o maior avatar do app — errar para
 * cima custa banda, errar para baixo custa a foto borrada que esta onda veio
 * consertar.
 */
const FALLBACK_AVATAR_PX = 112;

const LottieAvatar = lazy(() => import("./LottieAvatar"));

/** Só aparelho com hover DE VERDADE: no iOS um toque dispara `mouseenter`. */
function canHover(): boolean {
  return typeof window !== "undefined" && !!window.matchMedia?.("(hover: hover)").matches;
}

/**
 * Avatar Lottie fora do perfil próprio: `<img>` com o frame 0, sem nenhum
 * `<canvas>` na página (BUG-197 — ver `lottieStill.ts`). No desktop, o hover
 * ainda toca a animação, montando o player só enquanto o cursor está em cima.
 */
function LottieStill({
  src,
  alt,
  className,
  animateOnHover,
  fallback,
}: {
  src: string;
  alt: string;
  className: string;
  animateOnHover?: boolean;
  fallback: ReactNode;
}) {
  // O resultado carrega a URL a que pertence: avatar trocado volta a
  // "carregando" sozinho, sem setState síncrono no efeito.
  const [resolved, setResolved] = useState<{ src: string; still: string | null } | null>(() => {
    const cached = lottieStillCache.peek(src);
    return cached === undefined ? null : { src, still: cached };
  });
  const [hovering, setHovering] = useState(false);
  const still = resolved?.src === src ? resolved.still : undefined;

  useEffect(() => {
    if (still !== undefined) return;
    let alive = true;
    void lottieStillCache.get(src).then((result) => {
      if (alive) setResolved({ src, still: result });
    });
    return () => {
      alive = false;
    };
  }, [src, still]);

  const hoverEnabled = !!animateOnHover && canHover();
  const hoverProps = hoverEnabled
    ? { onMouseEnter: () => setHovering(true), onMouseLeave: () => setHovering(false) }
    : {};

  if (hoverEnabled && hovering) {
    return (
      <div className="contents" {...hoverProps}>
        <Suspense fallback={fallback}>
          <LottieAvatar src={src} alt={alt} className={className} animate />
        </Suspense>
      </div>
    );
  }
  if (!still) return <div className="contents" {...hoverProps}>{fallback}</div>;
  return (
    <img src={still} alt={alt} className={`${className} object-cover`} {...hoverProps} />
  );
}

export interface PresetAvatarConfig {
  key: string;
  emoji: string;
  from: string;  // hex
  to: string;    // hex
  label: string;
}

/** 8 opções de avatar nativo — armazenadas como "preset:<key>" no photoURL */
export const PRESET_AVATARS: PresetAvatarConfig[] = [
  { key: "avatar_01", emoji: "🦁", from: "#f59e0b", to: "#ea580c", label: "Leão" },
  { key: "avatar_02", emoji: "🐉", from: "#a855f7", to: "#4338ca", label: "Dragão" },
  { key: "avatar_03", emoji: "🌸", from: "#f9a8d4", to: "#f43f5e", label: "Flor" },
  { key: "avatar_04", emoji: "📚", from: "#4ade80", to: "#0d9488", label: "Livros" },
  { key: "avatar_05", emoji: "☕", from: "#b45309", to: "#78350f", label: "Café" },
  { key: "avatar_06", emoji: "🌙", from: "#3b82f6", to: "#1e1b4b", label: "Lua" },
  { key: "avatar_07", emoji: "🌿", from: "#84cc16", to: "#15803d", label: "Planta" },
  { key: "avatar_08", emoji: "⭐", from: "#facc15", to: "#d97706", label: "Estrela" },
];

const PRESET_MAP = Object.fromEntries(PRESET_AVATARS.map((p) => [p.key, p]));

interface AvatarDisplayProps {
  photoURL?: string;
  displayName: string;
  /** Classes aplicadas ao container (controla tamanho, shadow, etc.) */
  className?: string;
  /** Quando definido, substitui o ring padrão por um ring de gradiente PRO/Founder */
  proTier?: "pro" | "founder" | null;
  /** Frente G — id da moldura equipada (`users.activeAvatarBorder`). Tem
   *  prioridade sobre o ring de tier quando presente e conhecida. */
  avatarBorder?: string | null;
  /** Para `photoURL` Lottie: toca em loop assim que monta. Default: false. */
  animate?: boolean;
  /** Para `photoURL` Lottie: toca em loop enquanto o cursor está sobre. */
  animateOnHover?: boolean;
  /**
   * Lado renderizado em px CSS. Normalmente dispensável — é lido da própria
   * `className`. Passe explicitamente quando o tamanho não estiver na classe
   * (`h-full`) ou quando a superfície for exportada em alta (share card).
   */
  sizePx?: number;
}

function getInitials(name: string): string {
  return name
    .split(" ")
    .slice(0, 2)
    .map((w) => w[0]?.toUpperCase() ?? "")
    .join("");
}

const PRO_RING_STYLE = {
  background: "linear-gradient(135deg, #8b5cf6, #6d28d9)",
} as const;

/**
 * Halo dourado somado à moldura equipada de um Founder.
 *
 * Antes, moldura comprada substituía o anel de tier por completo e a
 * `animate-spin-slow` do fundador ia junto: equipar a moldura de 1200 moedas
 * DEIXAVA o avatar mais morto do que antes. Agora as duas coisas compõem — a
 * moldura escolhida manda no anel, o dourado do fundador continua respirando
 * por fora. Vale inclusive para molduras comuns, que não têm halo próprio.
 */
const FOUNDER_COMPOSED_GLOW = "0 0 14px rgba(251, 191, 36, 0.5), 0 0 30px rgba(217, 119, 6, 0.2)";

function AvatarInner({
  photoURL,
  displayName,
  className,
  animate,
  animateOnHover,
  sizePx,
}: {
  photoURL?: string;
  displayName: string;
  className: string;
  animate?: boolean;
  animateOnHover?: boolean;
  sizePx: number;
}) {
  const initials = getInitials(displayName || "L");
  const base = `rounded-full ${className}`;

  /**
   * URL que já falhou, não um booleano — foto trocada volta a ser tentada
   * sozinha, sem efeito e sem reset manual (mesmo padrão do `CoverThumb`,
   * BUG-84).
   *
   * Sem isto, `<img>` que responde erro fica desenhando o **texto alternativo**
   * dentro do círculo, para sempre: é o "Estante Cósmica (Mayara)" vazando por
   * cima do avatar no relato que abriu este bug. E não é hipotético que falhe —
   * `setProfileImage` **destrói** o asset anterior no Cloudinary a cada troca
   * (`functions/src/account/profileImage.ts`), enquanto liga, feed, "amigos
   * lendo", match e resenhas guardam uma CÓPIA da URL antiga. Toda cópia dessas
   * vira 404 permanente na primeira vez que a pessoa troca de foto.
   */
  const [failedUrl, setFailedUrl] = useState<string | null>(null);

  /**
   * Corpo do monograma (e do emoji de preset) derivado do lado do avatar.
   *
   * Os dois eram `text-3xl` **fixo** — 30px dentro de um círculo que em linha
   * de ranking, feed ou pódio tem 32px. Duas iniciais nesse tamanho não cabem,
   * e como o círculo não recortava nada elas **vazavam para fora dele**:
   * medido a 390px no pódio da `DivisionSheet`, "Vitória Guimarães Andrade"
   * saía com o "VG" transbordando a bola dos dois lados. É o que faz uma linha
   * bem construída parecer sem CSS.
   *
   * O `Math.min(30, …)` é o que torna isto **não-regressivo**: todo avatar de
   * ~75px para cima (hero do perfil, `AvatarPicker`, cards de exportação)
   * continua exatamente em 30px como hoje — só encolhe quem já estava
   * estourando. O `overflow-hidden` é o cinto: mesmo que alguém passe uma
   * classe de tamanho que o `pxFromSizeClassName` não saiba ler, a letra fica
   * contida em vez de escapar da bola.
   */
  const glyphPx = (ratio: number) => Math.min(30, Math.round(sizePx * ratio));
  const initialsStyle = { fontSize: glyphPx(0.4) };

  if (photoURL?.startsWith("preset:")) {
    const key = photoURL.slice(7);
    const preset = PRESET_MAP[key];
    if (preset) {
      return (
        <div
          className={`${base} flex items-center justify-center overflow-hidden`}
          style={{ background: `linear-gradient(135deg, ${preset.from}, ${preset.to})` }}
        >
          <span className="select-none leading-none" style={{ fontSize: glyphPx(0.55) }}>{preset.emoji}</span>
        </div>
      );
    }
  }

  if (photoURL && isLottieUrl(photoURL)) {
    const initialsFallback = (
      <div className={`${base} flex items-center justify-center overflow-hidden bg-linear-to-br from-primary to-secondary`}>
        <span className="font-heading font-bold leading-none text-background" style={initialsStyle}>{initials}</span>
      </div>
    );
    // ⚠️ BUG-197 (25/09): o player vivo na página é o gatilho medido do
    // congelamento na volta do fundo. Só quem PEDE animação (hero do perfil
    // próprio, Ajustes) monta o player; todo o resto mostra a foto parada.
    if (!animate) {
      return (
        <LottieStill
          src={photoURL}
          alt={displayName}
          className={base}
          animateOnHover={animateOnHover}
          fallback={initialsFallback}
        />
      );
    }
    return (
      <Suspense fallback={initialsFallback}>
        <LottieAvatar src={photoURL} alt={displayName} className={base} animate />
      </Suspense>
    );
  }

  if (photoURL && photoURL !== failedUrl) {
    return (
      <img
        // ⚠️ `animatedAware` — BUG-192. O avatar aceita GIF, e sem isto o
        // WebKit recebe WebP animado, que ele não anima. O avatar do FUNDADOR
        // escapa por acaso (é legado e a URL dele guardou a extensão `.gif`,
        // que faz o Cloudinary escolher mp4); todo avatar animado enviado pelo
        // editor de recorte nasce sem extensão e congela igual ao banner.
        src={cloudinaryImageUrl(photoURL, {
          width: sizePx,
          height: sizePx,
          animatedAware: true,
        })}
        alt={displayName}
        width={sizePx}
        height={sizePx}
        className={`${base} object-cover`}
        onError={() => setFailedUrl(photoURL)}
      />
    );
  }

  return (
    <div className={`${base} flex items-center justify-center overflow-hidden bg-linear-to-br from-primary to-secondary`}>
      <span className="font-heading font-bold leading-none text-background" style={initialsStyle}>{initials}</span>
    </div>
  );
}

export function AvatarDisplay({
  photoURL,
  displayName,
  className = "",
  proTier,
  // Renomeado no destructuring: o prop carrega o ID, e `avatarBorder` é o
  // resolver do catálogo importado acima.
  avatarBorder: avatarBorderId,
  animate,
  animateOnHover,
  sizePx,
}: AvatarDisplayProps) {
  // O lado é resolvido AQUI, não no `AvatarInner`: quando há moldura, o
  // filho recebe `h-full w-full` e a classe com o tamanho fica no wrapper.
  const resolvedPx = sizePx ?? pxFromSizeClassName(className) ?? FALLBACK_AVATAR_PX;

  // A composição tier × moldura mora inteira em `resolveAvatarBorderId` — aqui
  // só se desenha o que ela decidir. Ver o docblock dela para a precedência e
  // para os três dialetos que ela substituiu.
  //
  // ⚠️ Quando ela devolve `null` porque a pessoa escolheu "Nenhuma", o ramo de
  // `proTier` abaixo ainda desenha o anel da assinatura. É deliberado:
  // desligar a MOLDURA não é deixar de ser assinante, e o Founder que desliga o
  // latão continua com a placa Nº na linha do nome. Quem quer o avatar 100%
  // limpo passa `hideProTier`.
  const border = avatarBorder(resolveAvatarBorderId(avatarBorderId, proTier));

  if (border) {
    return (
      <AvatarBorderRing
        borderId={border.id}
        className={className}
        // O lado já está resolvido aqui; sem repassá-lo, o anel não tem como
        // saber que está num avatar de 28px.
        sizePx={resolvedPx}
        // O halo composto só faz sentido quando a moldura é OUTRA: somado à
        // própria moldura de Founder, seria o mesmo dourado duas vezes.
        extraGlow={
          proTier === "founder" && border.id !== FOUNDER_BORDER_ID ?
            FOUNDER_COMPOSED_GLOW :
            undefined
        }
      >
        <AvatarInner
          photoURL={photoURL}
          displayName={displayName}
          className="h-full w-full"
          animate={animate}
          animateOnHover={animateOnHover}
          sizePx={resolvedPx}
        />
      </AvatarBorderRing>
    );
  }

  if (proTier) {
    return (
      // O anel de tier escala junto com as molduras: `p-[3px]` chumbado dava
      // 10,7% do lado na linha da liga contra 2,7% no hero, e a assinatura
      // aparecia como contorno grosso exatamente onde o avatar é pequeno.
      <div
        className={`${className} rounded-full`}
        style={{ ...PRO_RING_STYLE, padding: resolveBorderRingPx(DEFAULT_BORDER_RING_PX, resolvedPx) }}
      >
        <AvatarInner
          photoURL={photoURL}
          displayName={displayName}
          className="h-full w-full"
          animate={animate}
          animateOnHover={animateOnHover}
          sizePx={resolvedPx}
        />
      </div>
    );
  }

  return (
    <AvatarInner
      photoURL={photoURL}
      displayName={displayName}
      className={className}
      animate={animate}
      animateOnHover={animateOnHover}
      sizePx={resolvedPx}
    />
  );
}
